/**
 * Шаг 1 wizard «Отрасль» (E07.1, ФТ-1.1).
 * Плитки: один клик выбирает, повторный снимает; пропуск не блокирует.
 * Визуал First screen (Figma): иконка + название + краткое описание.
 */

import { useEffect, useState } from "react";
import type { PublicIndustry } from "@potok/shared";
import type { PublicCatalogApi } from "../../api/catalog.api";
import { ApiRequestError } from "../../api/http";

export type IndustryStepProps = {
  api: PublicCatalogApi;
  selectedId: string | null;
  onSelect: (industryId: string | null) => void;
  onContinue: () => void;
};

type IndustryPresentation = {
  icon: string;
  description: string;
};

export function IndustryStep({ api, selectedId, onSelect, onContinue }: IndustryStepProps) {
  const [industries, setIndustries] = useState<PublicIndustry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .listIndustries()
      .then((rows) => {
        if (!cancelled) {
          setIndustries(rows);
        }
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setError(describeError(caught));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  function toggle(id: string): void {
    onSelect(selectedId === id ? null : id);
  }

  return (
    <section className="wizard-step wizard-step--industry" aria-labelledby="industry-step-title">
      <h2 id="industry-step-title" className="wizard-step__title">
        Выберите отрасль
      </h2>
      <p className="wizard-step__hint">
        Шаг 1 из 5 — Система подберёт подходящие модели конвейеров
      </p>

      {error ? (
        <p className="wizard-step__status wizard-step__status--error" role="alert">
          {error}
        </p>
      ) : null}
      {loading ? <p className="wizard-step__hint">Загрузка отраслей…</p> : null}

      {!loading && !error ? (
        <ul className="industry-grid">
          {industries.length === 0 ? (
            <li className="wizard-step__hint">Опубликованных отраслей пока нет</li>
          ) : (
            industries.map((industry) => {
              const selected = industry.id === selectedId;
              const presentation = presentIndustry(industry.name);
              return (
                <li key={industry.id}>
                  <button
                    type="button"
                    className={selected ? "industry-tile industry-tile--selected" : "industry-tile"}
                    aria-label={industry.name}
                    aria-pressed={selected}
                    onClick={() => toggle(industry.id)}
                  >
                    <span className="industry-tile__icon" aria-hidden="true">
                      {presentation.icon}
                    </span>
                    <span className="industry-tile__body">
                      <span className="industry-tile__name">{industry.name}</span>
                      <span className="industry-tile__desc">{presentation.description}</span>
                    </span>
                  </button>
                </li>
              );
            })
          )}
        </ul>
      ) : null}

      <div className="wizard-step__actions">
        <button type="button" className="wizard-step__primary" onClick={onContinue}>
          {selectedId === null ? "Пропустить" : "Далее"}
        </button>
      </div>
    </section>
  );
}

/** Локальная презентация по имени отрасли (API пока без icon/description). */
export function presentIndustry(name: string): IndustryPresentation {
  const normalized = name.toLowerCase();
  if (normalized.includes("горн")) {
    return { icon: "⛏", description: "Уголь, руда, нерудные материалы" };
  }
  if (normalized.includes("зерн") || normalized.includes("апк") || normalized.includes("агро")) {
    return { icon: "🌾", description: "Зерно, комбикорм, семена" };
  }
  if (normalized.includes("цемент") || normalized.includes("строй")) {
    return { icon: "🏗", description: "Цемент, шлак, зола-уноса, щебень" };
  }
  if (normalized.includes("хим")) {
    return { icon: "⚗", description: "Удобрения, соли, порошки" };
  }
  if (normalized.includes("металл")) {
    return { icon: "🔩", description: "Окатыши, агломерат, скрап" };
  }
  if (normalized.includes("энерг")) {
    return { icon: "⚡", description: "Уголь, зола, биомасса" };
  }
  if (normalized.includes("пищ")) {
    return { icon: "🌽", description: "Сахар, соль, крупы" };
  }
  if (normalized.includes("порт") || normalized.includes("логист")) {
    return { icon: "🚢", description: "Навалочные грузы, контейнерные терминалы" };
  }
  return { icon: "◆", description: "Отрасль применения" };
}

function describeError(error: unknown): string {
  if (error instanceof ApiRequestError) {
    return error.message;
  }
  return "Не удалось загрузить отрасли";
}
